import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "./store";
import { Inspector, Interactor } from "../entities/missionMap/missionMap";

interface RhapsodyReducerState {
  selectedToken?: string;
  bookletToken?: string;
  bookletJobStageID?: number;
  answers: {
    [token: string]: {
      inspectors?: {
        [i: number]: Inspector;
      };
      selectedInteractor?: Interactor;
      selectedInteractorJobStageID?: number;
    };
  };
  formOpen: { [key: string]: boolean };
}

const initialState = {
  answers: {},
  formOpen: {},
} as RhapsodyReducerState;

export const rhapsodyReducerSlice = createSlice({
  name: "rhapsody",
  initialState,
  reducers: {
    setSelectedToken(state, action: PayloadAction<string | undefined>) {
      state.selectedToken = action.payload;
    },
    setSelectedBookletToken(state, action: PayloadAction<string | undefined>) {
      if (!action.payload) state.bookletJobStageID = undefined;
      state.bookletToken = action.payload;
    },
    setBookletJobStageID(state, action: PayloadAction<number | undefined>) {
      state.bookletJobStageID = action.payload;
    },
    setSelectedInteractor(
      state,
      action: PayloadAction<Interactor | undefined>,
    ) {
      const params = new URLSearchParams(window.location.search);
      const token = state.selectedToken ?? params.get("token")!;
      const answer = state.answers[token] ?? {};

      if (token?.length > 0)
        state.answers[token] = {
          ...answer,
          selectedInteractor: action.payload,
        };
    },
    setSelectedInteractorJobStageID(
      state,
      action: PayloadAction<number | undefined>,
    ) {
      const params = new URLSearchParams(window.location.search);
      const token = state.selectedToken ?? params.get("token")!;
      const answer = state.answers[token] ?? {};

      if (token?.length > 0)
        state.answers[token] = {
          ...answer,
          selectedInteractorJobStageID: action.payload,
        };
    },
    setInspectors(
      state,
      action: PayloadAction<{
        [i: number]: Inspector;
      }>,
    ) {
      const params = new URLSearchParams(window.location.search);
      const token = state.selectedToken ?? params.get("token")!;
      const answer = state.answers[token] ?? {};

      if (token?.length > 0)
        state.answers[token] = {
          ...answer,
          inspectors: { ...answer.inspectors, ...action.payload },
        };
    },
    setAnswers(
      state,
      action: PayloadAction<{
        [token: string]: {
          inspectors?: {
            [i: number]: Inspector;
          };
          selectedInteractor?: Interactor;
          selectedInteractorJobStageID?: number;
        };
      }>,
    ) {
      state.answers = action.payload;
    },
    setFormOpen(
      state,
      action: PayloadAction<{ formID: FormID; isOpen: boolean }>,
    ) {
      const { formID, isOpen } = action.payload;
      state.formOpen[formID] = isOpen;
    },
  },
});

export const {
  setAnswers,
  setBookletJobStageID,
  setSelectedBookletToken,
  setSelectedInteractor,
  setSelectedInteractorJobStageID,
  setInspectors,
  setFormOpen,
  setSelectedToken,
} = rhapsodyReducerSlice.actions;

export const selectedInteractorSelector = createSelector(
  (s: RootState) =>
    s.rhapsody.answers[s.rhapsody.selectedToken ?? ""]?.selectedInteractor,
  (s) => s,
);

export const selectedInteractorJobStageIDSelector = createSelector(
  (s: RootState) =>
    s.rhapsody.answers[s.rhapsody.selectedToken ?? ""]
      ?.selectedInteractorJobStageID,
  (s) => s,
);

export const selectedBookletTokenSelector = createSelector(
  (s: RootState) => s.rhapsody.bookletToken,
  (s) => s,
);

export const bookletJobStageIDSelector = createSelector(
  (s: RootState) => s.rhapsody.bookletJobStageID,
  (s) => s,
);

export const selectedTokenSelector = createSelector(
  (s: RootState) => s.rhapsody.selectedToken,
  (s) => s,
);

export const answersSelector = createSelector(
  (s: RootState) => s.rhapsody.answers,
  (s) => s,
);

export const inspectorsSelector = createSelector(
  (s: RootState) => {
    const params = new URLSearchParams(window.location.search);
    const token = s.rhapsody.selectedToken ?? params.get("token")!;

    return s.rhapsody.answers[token]?.inspectors;
  },
  (hover) => hover ?? {},
);

export const formOpenSelector = (formID: FormID) => {
  return createSelector([(s) => s.rhapsody.formOpen], (s) => {
    return s[formID] ?? false;
  });
};

export type FormID = "askForRelease";

export const apiEndpointSelector = createSelector(
  (s: RootState) => s.networkState.networkApiValues.endpoint,
  (s) => s,
);
